import React from 'react';
import { Loader2Icon } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  loading?: boolean; // Support both naming conventions
  fullWidth?: boolean;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    children,
    variant = 'primary',
    size = 'md',
    leftIcon,
    rightIcon,
    onClick,
    disabled = false,
    type = 'button',
    className = '',
    fullWidth = false,
    isLoading = false,
    loading = false,
    ...props
  }, ref) => {
    // Support both loading prop names and extract non-DOM props
    const actualLoading = isLoading || loading;
    const { isLoading: _isLoading, loading: _loading, ...domProps } = { isLoading, loading, ...props };

    // Base classes (STYLEGUIDE.md §5 Button)
    const baseClasses = 'inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600/55 focus-visible:ring-offset-2';

    // Size classes — control heights 32 / 38 / 44
    const sizeClasses = {
      sm: 'h-8 px-3 text-body-sm',
      md: 'h-[38px] px-4 text-body',
      lg: 'h-11 px-5 text-[15px]'
    };

    // Variant classes
    const variantClasses = {
      primary: 'bg-navy-900 text-white hover:bg-navy-800',
      secondary: 'bg-white text-ink-700 border border-ink-300 hover:bg-ink-50',
      outline: 'bg-transparent text-navy-900 border border-navy-900 hover:bg-navy-50',
      danger: 'bg-danger-solid text-white hover:bg-danger-hover',
      ghost: 'bg-transparent text-ink-500 hover:bg-ink-100'
    };

    // Disabled classes
    const disabledClasses = 'opacity-45 pointer-events-none';
    
    // Full width class
    const widthClass = fullWidth ? 'w-full' : '';
    
    // Combine all classes
    const buttonClasses = `
      ${baseClasses} 
      ${sizeClasses[size]} 
      ${variantClasses[variant]} 
      ${disabled || actualLoading ? disabledClasses : ''} 
      ${widthClass}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <button 
        ref={ref}
        className={buttonClasses} 
        disabled={disabled || actualLoading} 
        onClick={onClick} 
        type={type} 
        {...domProps}
      >
        {actualLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
        {!actualLoading && leftIcon && <span className="inline-flex">{leftIcon}</span>}
        {children}
        {rightIcon && <span className="inline-flex">{rightIcon}</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;